import React, { useCallback } from 'react';
import { CheckIcon } from '@heroicons/react/solid';

import { PatientSelectionCardProps } from '../types/patient-management.types';

function PatientSelectionCard({ patient, onSelect, isSelected, isCurrentPatient = false }: PatientSelectionCardProps) {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect(patient);
      }
    },
    [onSelect, patient],
  );

  const getCardStyles = () => {
    if (isCurrentPatient && isSelected) {
      return 'bg-green-50 border-green-500 shadow-md ring-2 ring-green-200';
    }
    if (isCurrentPatient) {
      return 'bg-green-50 border-green-400 ring-1 ring-green-200 hover:bg-green-100';
    }
    if (isSelected) {
      return 'bg-indigo-50 border-indigo-500 shadow-md';
    }

    return 'border-gray-200 hover:bg-gray-50 hover:border-gray-300';
  };

  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:outline-none ${getCardStyles()}`}
      onClick={() => onSelect(patient)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Select patient ${patient.name} ${patient.lastName}${
        isCurrentPatient ? ' (Currently assigned)' : ''
      }`}
      aria-pressed={isSelected}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-medium text-gray-900">
              {patient.name} {patient.lastName}
            </h3>
            {isCurrentPatient && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Currently Assigned
              </span>
            )}
            {isSelected && (
              <div
                className={`flex items-center justify-center w-5 h-5 rounded-full ${
                  isCurrentPatient ? 'bg-green-600' : 'bg-indigo-600'
                }`}
              >
                <CheckIcon className="w-3 h-3 text-white" aria-hidden="true" />
              </div>
            )}
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>ID: {patient.id}</span>
              {patient.isChild && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Child
                </span>
              )}
            </div>

            {patient.email && <div className="text-sm text-gray-500">Email: {patient.email}</div>}

            {patient.phone && <div className="text-sm text-gray-500">Phone: {patient.phone}</div>}

            {(patient.customerName || patient.customerLastName) && (
              <div className="text-sm text-gray-500">
                Customer: {patient.customerName} {patient.customerLastName}
                {patient.customerCity && `, ${patient.customerCity}`}
              </div>
            )}

            <div className="text-xs text-gray-400">Created: {new Date(patient.createdAt).toLocaleDateString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PatientSelectionCard;
